import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Switch } from '@/src/shadComponents/ui/switch';
import { useEventContext } from '../components/EventContext';
import useRekognition, {
    RekognitionLabel,
} from '@/src/Activations/components/hooks/useRekognition';

/*
 * Analyze — media analysis settings + what it found (ADMIN-MERGE
 * Phase C; classic's #analyze tab). Results group by label with
 * thumbnails from the shared media cache instead of classic's
 * unpaginated Media-ID table; public lookups consume the labels via
 * the gallery's /lookup pages.
 */

const DETECT_OPTIONS = [
    { value: 'number', label: 'Numbers (bibs, tags)' },
    { value: 'text', label: 'Text' },
    { value: 'face', label: 'Faces' },
];

export default function AnalyzeV2() {
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const navigate = useNavigate();
    const rekognition = useRekognition();
    const { ensureMedia, media } = useEventContext();

    const [settingsId, setSettingsId] = useState<number | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [type, setType] = useState('number');
    const [confidence, setConfidence] = useState(97);
    const [labels, setLabels] = useState<RekognitionLabel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);
    const [expanded, setExpanded] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        ensureMedia();
        Promise.all([
            rekognition.getSettings(id).catch(() => null),
            rekognition.getLabels(id).catch(() => null),
        ]).then(([settingsResponse, labelsResponse]) => {
            if (!mounted) return;
            const settings = settingsResponse?.data as any;
            if (settings?.id) {
                setSettingsId(settings.id);
                setEnabled(!!settings.enabled);
                setType(settings.type ?? 'number');
                setConfidence(Number(settings.confidence) || 97);
            }
            setLabels(labelsResponse?.data ?? []);
            setIsLoading(false);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const mediaById = useMemo(() => {
        const map = new Map<number, any>();
        (media ?? []).forEach((m: any) => map.set(m.id, m));
        return map;
    }, [media]);

    const groups = useMemo(() => {
        const byLabel = new Map<string, RekognitionLabel[]>();
        labels.forEach((row) => {
            const key = row.label ?? '—';
            byLabel.set(key, [...(byLabel.get(key) ?? []), row]);
        });
        return Array.from(byLabel.entries())
            .map(([label, items]) => ({ label, items }))
            .sort((a, b) => b.items.length - a.items.length);
    }, [labels]);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            if (settingsId) {
                await rekognition.updateSettings(
                    settingsId,
                    enabled,
                    type,
                    confidence,
                );
            } else {
                const response = await rekognition.createSettings(
                    id,
                    enabled,
                    type,
                    confidence,
                );
                setSettingsId((response.data as any)?.id ?? null);
            }
            toast.success('Analysis settings saved');
        } catch {
            toast.error("Couldn't save the settings.");
        }
        setSaving(false);
    };

    const run = async () => {
        if (running) return;
        setRunning(true);
        const ok = await rekognition
            .runRekognition(id)
            .then(() => true)
            .catch(() => false);
        setRunning(false);
        if (ok)
            toast.success(
                'Analyzing — results appear here as photos process.',
            );
        else toast.error("Couldn't start the analysis.");
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[860px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-20 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[860px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-4">
                <button
                    type="button"
                    onClick={() => navigate(`/v2/activations/${id}/media`)}
                    className="ps-label cursor-pointer border-0 bg-transparent p-0 hover:text-foreground"
                >
                    Media
                </button>
                <span className="mx-1.5 opacity-60">/</span>
                <span className="text-muted-foreground">Analyze</span>
            </div>

            <section className="mb-9">
                <div className="mb-3 flex flex-wrap items-end gap-x-5 gap-y-3">
                    <label className="flex cursor-pointer items-center gap-2.5 text-[13px] font-semibold">
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                        />
                        Analyze uploads
                    </label>
                    <div>
                        <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                            Detect
                        </label>
                        <select
                            className="ps-input cursor-pointer appearance-none text-[13px]"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                        >
                            {DETECT_OPTIONS.map((option) => (
                                <option
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                            Confidence %
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={100}
                            className="ps-input w-[84px] text-[13px] tabular-nums"
                            value={confidence}
                            onChange={(e) =>
                                setConfidence(+e.target.value)
                            }
                        />
                    </div>
                    <div className="flex items-center gap-2.5">
                        <button
                            type="button"
                            disabled={saving}
                            onClick={save}
                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        {settingsId && (
                            <button
                                type="button"
                                disabled={running}
                                onClick={run}
                                className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-foreground disabled:opacity-50"
                            >
                                {running
                                    ? 'Starting…'
                                    : 'Analyze existing photos'}
                            </button>
                        )}
                    </div>
                </div>
                <p className="max-w-[62ch] text-[12px] text-faint">
                    New uploads analyze automatically while on. Labels feed
                    the gallery's lookup pages.
                </p>
            </section>

            <section>
                <div className="ps-label mb-2">
                    Detected · {groups.length.toLocaleString()} label
                    {groups.length === 1 ? '' : 's'} ·{' '}
                    {labels.length.toLocaleString()} match
                    {labels.length === 1 ? '' : 'es'}
                </div>
                {groups.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        Nothing detected yet
                        {enabled
                            ? ' — labels appear as photos upload.'
                            : ' — turn analysis on and save.'}
                    </p>
                )}
                {groups.map((group) => {
                    const open = expanded === group.label;
                    const shown = open
                        ? group.items
                        : group.items.slice(0, 8);
                    return (
                        <div
                            key={group.label}
                            className="border-t border-line py-3"
                        >
                            <div className="mb-2 flex items-baseline gap-2.5">
                                <span className="font-mono text-[12px] font-semibold">
                                    {group.label}
                                </span>
                                <span className="text-[11.5px] text-faint">
                                    {group.items.length} photo
                                    {group.items.length === 1 ? '' : 's'}
                                </span>
                                {group.items.length > 8 && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setExpanded(
                                                open ? null : group.label,
                                            )
                                        }
                                        className="ml-auto cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-muted-foreground hover:text-foreground"
                                    >
                                        {open
                                            ? 'show less'
                                            : `show all ${group.items.length}`}
                                    </button>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {shown.map((row) => {
                                    const m = mediaById.get(row.mediaId);
                                    return (
                                        <button
                                            key={row.id}
                                            type="button"
                                            title={`Media ${row.mediaId} — open Media`}
                                            onClick={() =>
                                                navigate(
                                                    `/v2/activations/${id}/media`,
                                                )
                                            }
                                            className="h-[52px] w-[52px] cursor-pointer overflow-hidden rounded-[7px] border border-border bg-secondary p-0"
                                        >
                                            {m ? (
                                                <img
                                                    src={
                                                        m.thumbnailUri ??
                                                        m.originalUri
                                                    }
                                                    alt=""
                                                    loading="lazy"
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <span className="font-mono text-[9px] text-faint">
                                                    #{row.mediaId}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </section>
        </main>
    );
}
